import React, { useState } from 'react';
import {
  ArrowUpTrayIcon,
  XMarkIcon,
  PlusIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  InformationCircleIcon
} from '@heroicons/react/24/outline';
import toast from '../utils/toast';
import api from '../services/api';

const TrademarkImport = () => {
  const [trademarkNumbers, setTrademarkNumbers] = useState(['']);
  const [importType, setImportType] = useState('inbound');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  // Add new trademark number input field
  const addTrademarkNumber = () => {
    setTrademarkNumbers([...trademarkNumbers, '']);
  };

  // Remove trademark number input field
  const removeTrademarkNumber = (index) => {
    if (trademarkNumbers.length > 1) {
      const newNumbers = trademarkNumbers.filter((_, i) => i !== index);
      setTrademarkNumbers(newNumbers);
    }
  };

  // Update trademark number at specific index
  const updateTrademarkNumber = (index, value) => {
    const newNumbers = [...trademarkNumbers];
    newNumbers[index] = value;
    setTrademarkNumbers(newNumbers);
  };

  // Handle import submission
  const handleImport = async () => {
    // Validate inputs
    const validNumbers = trademarkNumbers.filter(num => num.trim() !== '');
    
    if (validNumbers.length === 0) {
      toast.error('Please enter at least one trademark number');
      return;
    }

    if (!importType) {
      toast.error('Please select an import type');
      return;
    }

    setLoading(true);
    setError(null);
    setResult(null);

    try {
      console.log('📡 Starting trademark import...');
      console.log('📋 Import data:', {
        type: importType,
        trademarkNumbers: validNumbers
      });

      // Build URL with token placeholder
      let url = `https://agent3.tmpilot.com/api/global/import?token={token}&type=${importType}`;
      
      // Add trademark numbers as app[] parameters to URL
      validNumbers.forEach(number => {
        url += `&app[]=${encodeURIComponent(number.trim())}`;
      });

      console.log('🔗 Request URL:', url);

      const response = await api.post('/api-data/get-data', {
        url: url,
        method: 'POST' // Specify POST method for import API
      });

      if (response.data.success) {
        console.log('✅ Import result received:', response.data.data);
        setResult(response.data.data);
        toast.success('Trademark import completed successfully!');
      } else {
        throw new Error(response.data.message || 'Import failed');
      }
    } catch (err) {
      console.error('❌ Import error:', err);
      setError(err.message || 'Failed to import trademarks');
      toast.error('Import failed: ' + (err.message || 'Unknown error'));
    } finally {
      setLoading(false);
    }
  };

  // Clear all data
  const handleClear = () => {
    setTrademarkNumbers(['']);
    setImportType('inbound');
    setResult(null);
    setError(null);
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Trademark Import</h1>
              <p className="mt-2 text-sm text-gray-600">
                Import multiple trademark numbers with specified type (Inbound/Outbound)
              </p>
            </div>
            <div className="flex items-center space-x-3">
              <button
                onClick={handleClear}
                className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                <XMarkIcon className="h-4 w-4 mr-2" />
                Clear
              </button>
            </div>
          </div>
        </div>

        {/* Import Form */}
        <div className="bg-white shadow rounded-lg p-6 mb-6">
          <div className="space-y-6">
            {/* Import Type Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Import Type *
              </label>
              <div className="flex space-x-4">
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="importType"
                    value="inbound"
                    checked={importType === 'inbound'}
                    onChange={(e) => setImportType(e.target.value)}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                  />
                  <span className="ml-2 text-sm text-gray-700">Inbound</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="importType"
                    value="outbound"
                    checked={importType === 'outbound'}
                    onChange={(e) => setImportType(e.target.value)}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                  />
                  <span className="ml-2 text-sm text-gray-700">Outbound</span>
                </label>
              </div>
            </div>

            {/* Trademark Numbers */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Trademark Numbers *
              </label>
              <div className="space-y-3">
                {trademarkNumbers.map((number, index) => (
                  <div key={index} className="flex items-center space-x-3">
                    <input
                      type="text"
                      value={number}
                      onChange={(e) => updateTrademarkNumber(index, e.target.value)}
                      placeholder="Enter trademark number"
                      className="flex-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                    />
                    {trademarkNumbers.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeTrademarkNumber(index)}
                        className="inline-flex items-center p-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-red-700 bg-red-100 hover:bg-red-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                      >
                        <XMarkIcon className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                ))}
                <button
                  type="button"
                  onClick={addTrademarkNumber}
                  className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  <PlusIcon className="h-4 w-4 mr-2" />
                  Add Another Number
                </button>
              </div>
              <p className="mt-2 text-sm text-gray-500">
                Enter one trademark number per line. Empty fields will be ignored.
              </p>
            </div>

            {/* Import Button */}
            <div className="pt-4">
              <button
                onClick={handleImport}
                disabled={loading}
                className="w-full inline-flex justify-center items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Importing...
                  </>
                ) : (
                  <>
                    <ArrowUpTrayIcon className="h-4 w-4 mr-2" />
                    Import Trademarks
                  </>
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Results Section */}
        {result && (
          <div className="bg-white shadow rounded-lg p-6 mb-6">
            <div className="flex items-center mb-4">
              <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2" />
              <h3 className="text-lg font-medium text-gray-900">Import Results</h3>
            </div>
            <div className="bg-gray-50 rounded-md p-4">
              <pre className="text-sm text-gray-800 whitespace-pre-wrap overflow-x-auto">
                {JSON.stringify(result, null, 2)}
              </pre>
            </div>
          </div>
        )}

        {/* Error Section */}
        {error && (
          <div className="bg-white shadow rounded-lg p-6 mb-6">
            <div className="flex items-center mb-4">
              <ExclamationTriangleIcon className="h-5 w-5 text-red-500 mr-2" />
              <h3 className="text-lg font-medium text-gray-900">Import Error</h3>
            </div>
            <div className="bg-red-50 border border-red-200 rounded-md p-4">
              <p className="text-sm text-red-800">{error}</p>
            </div>
          </div>
        )}

        {/* Information Section */}
        <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
          <div className="flex">
            <InformationCircleIcon className="h-5 w-5 text-blue-400 mr-2 mt-0.5" />
            <div>
              <h3 className="text-sm font-medium text-blue-800">How it works</h3>
              <div className="mt-2 text-sm text-blue-700">
                <p>• Select the import type (Inbound or Outbound)</p>
                <p>• Enter one or more trademark numbers</p>
                <p>• Click "Import Trademarks" to process the import</p>
                <p>• The system will call the external API and display the results</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TrademarkImport; 